import { UiButtonComponent } from '@/ui/button/ui-button';
import { UiCheckboxComponent } from '@/ui/checkbox/ui-checkbox';
import { UiTableModule } from '@/ui/table/ui-table.module';
import { ApplyPureFunctionPipe } from '@/utils/angular/pipes/apply-pure-function.pipe';
import { legacyFormatStringDate } from '@/utils/temporal/legacy-string-date-formatter.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { Component, inject, type OnInit, output, Renderer2, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, firstValueFrom, type Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { sseTurquoiseColor } from '../../constants/color';
import type { ClassificationNodeId } from '../../interfaces/Classification.interface';
import { ConflictResolutionStrategy, type DmsFile } from '../../interfaces/DocumentManagementSystem.interface';
import { UnitCode } from '../../interfaces/Unit.interface';
import { DocumentManagementSystemProvider } from '../../providers/document-management-system/document-management-system.provider';
import {
    type AdvizeoModalButton,
    type AdvizeoModalComponent,
    injectAdvizeoModalInput
} from '../advizeo-modal-spawner/advizeo-modal-spawner.component';
import { ProgressBarComponent } from '../progress-bar/progress-bar';

interface FileComparison {
    title: string;
    importedFileValue: string;
    existingFileValue: string;
}

@Component({
    styleUrls: ['./document-management-system-import-modal.scss'],
    selector: 'document-management-system-import-modal',
    templateUrl: 'document-management-system-import-modal.html',
    imports: [
        ProgressBarComponent,
        UiTableModule,
        UiButtonComponent,
        UiCheckboxComponent,
        FormsModule,
        AsyncPipe,
        TranslatePipe,
        ApplyPureFunctionPipe
    ]
})
export class DocumentManagementSystemImportModalComponent
    implements AdvizeoModalComponent<{ files: File[]; classificationNodeId: ClassificationNodeId; title?: string }, null>, OnInit
{
    private _translateService = inject(TranslateService);
    private _dmsProvider = inject(DocumentManagementSystemProvider);
    private _renderer = inject(Renderer2);

    title = signal('');
    isDismissable = signal(false);
    buttons = signal<AdvizeoModalButton[]>([]);

    input = injectAdvizeoModalInput<{ files: File[]; classificationNodeId: ClassificationNodeId; title?: string }>();
    close = output<void>();

    @ViewChild('skip') skipBtn!: any;
    @ViewChild('keepBoth') keepBothBtn!: any;
    @ViewChild('replace') replaceBtn!: any;

    headers: string[] = ['title', 'importedFileValue', 'existingFileValue'];
    inputData$ = new BehaviorSubject<FileComparison[]>([]);

    limitSizeInB: number = 0;
    private fileLength: number = 0;
    private fileCount$: BehaviorSubject<number> = new BehaviorSubject(0);
    currentSizeInB$: BehaviorSubject<number> = new BehaviorSubject(0);
    progress$!: Observable<number>;
    progressColor: string = sseTurquoiseColor;
    isExistFile$ = new BehaviorSubject(false);
    currentFileName$ = new BehaviorSubject<string | null>(null);
    processStatusTranslation$!: Observable<string>;
    isDownloadDone$ = new BehaviorSubject(false);

    changeDefaultConflictResolutionStrategy: Boolean = false;
    defaultConflictResolutionStrategy: ConflictResolutionStrategy = ConflictResolutionStrategy.NONE;

    ngOnInit() {
        this.title.set(
            this.input().title ? this.input().title : this._translateService.instant('DOCUMENT_MANAGEMENT_SYSTEM_IMPORT.MODAL.TITLE')
        );

        this.fileLength = this.input().files.length;
        this.input().files.forEach(file => (this.limitSizeInB += file.size));

        this.progress$ = this.currentSizeInB$.pipe(map(currentSizeInB => Math.min((currentSizeInB * 100) / this.limitSizeInB, 100)));

        this.processStatusTranslation$ = combineLatest([this.currentFileName$, this.fileCount$, this.isExistFile$]).pipe(
            filter(([currentFileName, _]) => isNotNullish(currentFileName)),
            map(([currentFileName, fileCount, isExistFile]) => {
                if (isExistFile) {
                    return this._translateService.instant('DOCUMENT_MANAGEMENT_SYSTEM_IMPORT.MODAL.IS_EXIST', {
                        fileName: currentFileName
                    });
                } else {
                    return this._translateService.instant('DOCUMENT_MANAGEMENT_SYSTEM_IMPORT.MODAL.IN_PROGRESS', {
                        fileCount,
                        fileLength: this.fileLength,
                        fileName: currentFileName
                    });
                }
            })
        );

        this.processFiles();
    }

    private async processFiles() {
        for await (const file of this.input().files) {
            await this.processFile(file);
        }
        this.isDownloadDone$.next(true);
        this._dmsProvider.notifyFileChange(this.input().classificationNodeId);
    }

    private async processFile(file: File) {
        const count = this.fileCount$.getValue();
        this.fileCount$.next(count + 1);
        this.currentFileName$.next(file.name);
        await firstValueFrom(this._dmsProvider.addFile$(this.input().classificationNodeId, file, this.defaultConflictResolutionStrategy))
            .catch(async err => {
                this.isExistFile$.next(true);
                if (this.defaultConflictResolutionStrategy !== ConflictResolutionStrategy.NONE) {
                    await this.getDisplayData(file).then(displayData => this.inputData$.next(displayData));
                }
                if (this._dmsProvider.isFileNameAlreadyUsedError(err)) {
                    await this.handleConflictStrategy(file);
                }
            })
            .finally(() => {
                this.isExistFile$.next(false);
                this.increaseCurrentSize(file.size);
            });
    }

    private async getDisplayData(importFile: File): Promise<FileComparison[]> {
        const existFile: DmsFile = await this._dmsProvider.getFileByName(this.input().classificationNodeId, importFile.name);
        return [
            {
                title: this._translateService.instant('DOCUMENT_MANAGEMENT_SYSTEM_IMPORT.MODAL.ID_NAME'),
                importedFileValue: importFile.name,
                existingFileValue: existFile.fileName
            },
            {
                title: this._translateService.instant('DOCUMENT_MANAGEMENT_SYSTEM_IMPORT.MODAL.ID_SIZE'),
                importedFileValue: this._dmsProvider.formatFileSize(importFile.size, UnitCode.BYTE),
                existingFileValue: this._dmsProvider.formatFileSize(existFile.size, UnitCode.BYTE)
            },
            {
                title: this._translateService.instant('DOCUMENT_MANAGEMENT_SYSTEM_IMPORT.MODAL.ID_LAST_UPDATE'),
                importedFileValue: legacyFormatStringDate(importFile.lastModified, this._translateService.instant('DATE_FORMAT.DETAILED')),
                existingFileValue: legacyFormatStringDate(existFile.lastUpdatedAt, this._translateService.instant('DATE_FORMAT.DETAILED'))
            }
        ];
    }

    private handleConflictStrategy(file: File): Promise<void> {
        return (
            this.defaultConflictResolutionStrategy !== ConflictResolutionStrategy.NONE
                ? Promise.resolve(this.defaultConflictResolutionStrategy)
                : Promise.race([
                      new Promise<ConflictResolutionStrategy>(resolve => {
                          this._renderer.listen(this.skipBtn._element.nativeElement, 'click', () => {
                              resolve(ConflictResolutionStrategy.CANCEL);
                          });
                      }),
                      new Promise<ConflictResolutionStrategy>(resolve => {
                          this._renderer.listen(this.keepBothBtn._element.nativeElement, 'click', () => {
                              resolve(ConflictResolutionStrategy.KEEP_BOTH);
                          });
                      }),
                      new Promise<ConflictResolutionStrategy>(resolve => {
                          this._renderer.listen(this.replaceBtn._element.nativeElement, 'click', () => {
                              resolve(ConflictResolutionStrategy.REPLACE);
                          });
                      })
                  ])
        ).then(conflictResolutionStrategyCode => {
            if (this.changeDefaultConflictResolutionStrategy) {
                this.defaultConflictResolutionStrategy = conflictResolutionStrategyCode;
            }

            switch (conflictResolutionStrategyCode) {
                case ConflictResolutionStrategy.CANCEL:
                    return;
                case ConflictResolutionStrategy.KEEP_BOTH:
                case ConflictResolutionStrategy.REPLACE:
                    return firstValueFrom(
                        this._dmsProvider.addFile$(this.input().classificationNodeId, file, conflictResolutionStrategyCode)
                    ).then(() => {});
            }
        });
    }

    private increaseCurrentSize(size: number) {
        const currentSize = this.currentSizeInB$.getValue();
        this.currentSizeInB$.next(currentSize + size);
    }

    formatFileSizeFromBytes = (fileSizeInB: number): string => {
        return this._dmsProvider.formatFileSize(fileSizeInB, UnitCode.BYTE);
    };

    toggleCheckOfRepeatConflictStrategy(isChecked: boolean) {
        this.changeDefaultConflictResolutionStrategy = isChecked;
    }
}
